import React, { Component } from "react";
import { PrismDialog, PrismButton, PrismInputText } from "@prism-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

class EducationForm extends Component {
  state = {
    application_id: this.props.application.id,
    description: "",
    attainment_year: "",
    showDialog: false,
    editIndex: null, // null = adding new, number = editing existing
    originalEntry: null,
  };

  handleSave = async () => {
    if (this.state.editIndex !== null) {
      // Update existing
      await this.props.updateEducation(this.state.editIndex, {
        description: this.state.description,
        attainment_year: this.state.attainment_year,
      });
    } else {
      // Create new
      await this.props.createEducation(this.state);
    }
    this.setState({ description: "", attainment_year: "", editIndex: null, originalEntry: null });
  };

  handleEdit = (index) => {
    const entry = this.props.educationEntries[index];
    const snapshot = {
      description: entry.description || "",
      attainment_year: String(entry.attainment_year || ""),
    };
    this.setState({
      ...snapshot,
      editIndex: index,
      originalEntry: snapshot,
    });
  };

  handleDelete = (index) => {
    this.props.handleDeleteEducation(index);
    // If we were editing this entry, clear the form
    if (this.state.editIndex === index) {
      this.setState({ description: "", attainment_year: "", editIndex: null, originalEntry: null });
    } else if (this.state.editIndex !== null && this.state.editIndex > index) {
      // Adjust index if a preceding entry was deleted
      this.setState({ editIndex: this.state.editIndex - 1 });
    }
  };

  cancelEdit = () => {
    this.setState({ description: "", attainment_year: "", editIndex: null, originalEntry: null });
  };

  handlePrismInput = (name) => (e) => {
    const value = e.detail?.value ?? e.detail ?? '';
    this.setState({ [name]: value });
  };

  openDialog = () => {
    this.setState({ showDialog: true, description: "", attainment_year: "", editIndex: null, originalEntry: null });
  };

  closeDialog = () => {
    this.setState({ showDialog: false, description: "", attainment_year: "", editIndex: null, originalEntry: null });
  };

  isFieldDirty = (name) => {
    const { originalEntry } = this.state;
    if (!originalEntry) return false;
    return this.state[name] !== originalEntry[name];
  };

  getDirtyFields = () => {
    if (!this.state.originalEntry) return [];
    return ['description', 'attainment_year'].filter(f => this.isFieldDirty(f));
  };

  render() {
    const { educationEntries } = this.props;
    const { editIndex, description, attainment_year } = this.state;
    const isEditing = editIndex !== null;
    const dirtyFields = this.getDirtyFields();
    const hasDirtyFields = dirtyFields.length > 0;

    const canSave =
      description.trim().length > 0 &&
      attainment_year.trim().length === 4 &&
      /^\d{4}$/.test(attainment_year);

    return (
      <div>
        <PrismButton tone="primary" display="outline" onPrismClick={this.openDialog}>
          <FontAwesomeIcon icon={faPencil} style={{ marginRight: '0.4rem' }} />
          Manage Education
        </PrismButton>

        <PrismDialog
          open={this.state.showDialog}
          setAriaLabel="Manage Education"
          close-method="user"
          onPrismClose={this.closeDialog}
        >
          <span slot="header">Manage Education</span>

          {/* Entry list */}
          <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
            {educationEntries.length === 0 ? (
              <p style={{ color: 'var(--color-text-faded)', fontStyle: 'italic' }}>
                No education entries yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[...educationEntries]
                  .map((entry, idx) => ({ ...entry, _idx: idx }))
                  .sort((a, b) => (b.attainment_year || 0) - (a.attainment_year || 0))
                  .map((entry) => (
                  <div
                    key={entry.id || entry._idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '4px',
                      backgroundColor: editIndex === entry._idx ? 'var(--color-surface-alt)' : 'var(--color-surface)',
                      border: editIndex === entry._idx ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                      boxShadow: editIndex === entry._idx ? '0 0 0 1px var(--color-primary)' : 'none',
                      transition: 'border 0.15s, box-shadow 0.15s, background-color 0.15s',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{entry.description}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)' }}>
                        {entry.attainment_year}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => this.handleEdit(entry._idx)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-primary)', fontSize: '0.9rem', padding: '0.25rem 0.4rem',
                        }}
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => this.handleDelete(entry._idx)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-danger)', fontSize: '0.9rem', padding: '0.25rem 0.4rem',
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add/Edit form */}
          <div style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: '1rem',
          }}>
            <h6 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
              {isEditing ? 'Edit Entry' : 'Add New Entry'}
            </h6>
            {isEditing && hasDirtyFields && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.8rem', color: 'var(--color-warning)',
                marginBottom: '0.75rem',
              }}>
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px',
                  borderRadius: '50%', backgroundColor: 'var(--color-warning)',
                }} />
                {dirtyFields.length} unsaved {dirtyFields.length === 1 ? 'change' : 'changes'}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{
                borderLeft: isEditing && this.isFieldDirty('description') ? '3px solid var(--color-warning)' : '3px solid transparent',
                paddingLeft: '0.5rem', transition: 'border-color 0.15s',
              }}>
                <PrismInputText
                  label="Institution / Degree Earned"
                  name="description"
                  value={description}
                  required
                  onPrismInput={this.handlePrismInput('description')}
                />
              </div>
              <div style={{
                borderLeft: isEditing && this.isFieldDirty('attainment_year') ? '3px solid var(--color-warning)' : '3px solid transparent',
                paddingLeft: '0.5rem', transition: 'border-color 0.15s',
              }}>
                <PrismInputText
                  label="Year Attained"
                  name="attainment_year"
                  value={attainment_year}
                  required
                  maxlength={4}
                  onPrismInput={this.handlePrismInput('attainment_year')}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  style={{
                    backgroundColor: canSave ? 'var(--color-primary)' : 'var(--color-disabled)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.4rem 1rem',
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    fontSize: '0.85rem',
                    opacity: canSave ? 1 : 0.6,
                  }}
                  disabled={!canSave}
                  onClick={this.handleSave}
                >
                  {isEditing ? 'Update' : 'Add'}
                </button>
                <span style={{ display: isEditing ? undefined : 'none' }}>
                  <button
                    type="button"
                    onClick={this.cancelEdit}
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--color-text)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      padding: '0.4rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    Cancel
                  </button>
                </span>
              </div>
            </div>
          </div>

          <div slot="footer">
            <PrismButton display="outline" onPrismClick={this.closeDialog}>
              Done
            </PrismButton>
          </div>
        </PrismDialog>
      </div>
    );
  }
}

export default EducationForm;
